import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, finalize, Subscription, tap, throwError } from 'rxjs';
import { WorkspaceService } from '@workspaces/services/workspace.service';
import { getAllPathParams } from '@workspaces/utils/get-all-path-params';

@Component({
  selector: 'app-workspace-accept',
  templateUrl: './workspace-accept.component.html',
  styleUrls: ['./workspace-accept.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    RouterModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule
  ]
})
export class WorkspaceAcceptComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();

  public token: string | null = null;
  public isLoading = false;
  public isSuccess = false;
  public error: string | null = null;
  public workspaceId = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly workspaceService: WorkspaceService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
    const params = getAllPathParams(this.route.snapshot);
    this.workspaceId = params['workspaceId'] ?? 'null';

    if (!this.token || !this.workspaceId) {
      this.error = 'Invalid invitation link. No token provided.';
    }
  }

  onAccept(): void {
    if (!this.token || !this.workspaceId) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.sub.add(
      this.workspaceService
        .acceptInvitation(this.workspaceId, this.token)
        .pipe(
          tap(workspaceId => {
            this.isSuccess = true;
            this.snackBar.open('Successfully joined the workspace!', 'Close', { duration: 3000 });

            // Redirect after a short delay to let user see success state
            setTimeout(() => {
              this.router.navigate(['/workspaces', workspaceId, 'home'], { replaceUrl: true });
            }, 1500);
          }),
          catchError(err => {
            this.error = 'Failed to join workspace. The invitation may have expired.';
            this.snackBar.open(this.error, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
            return throwError(() => err);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
